import { EVENT_CATEGORIES, EVENT_NAMES } from '#~/analytics/constants/index.js';
import { resolveDeviceAnalyticsCatalogContextFromModel } from '#~/analytics/device-context.js';
import { isEmbeddedDiscovery, resolveEmbeddedFlowProps } from '#~/analytics/domains/index.js';
import { useAnalytics } from '#~/analytics/hooks/index.js';
import * as _Footer from '#~/common/editor/footer/index.js';
import { Sel, STag } from '#~/stag/tag.js';
import { isRtsp } from '@studio/models/video/rtsp';
import { useLatestFn } from '@studio/react-utils/hooks';
import type { ReactElement } from 'react';
import { useProducer, useStore } from '../store/hooks.js';
import { Panel } from '../store/types.js';
import { useWiring } from '../wiring.js';

/** Control `<Footer>` component. */
export const Footer = (props: _Footer.Props): ReactElement => {
  const { isReady } = useStore((µ) => µ.control);
  const producer = useProducer();
  const wiring = useWiring();
  const finalSave = useStore((µ) => µ.finalSave);
  const blocked = !!finalSave && (finalSave.rawPending || finalSave.status !== 'retryable');
  const sourceSelection = useStore((µ) => µ.source.selection);
  const controlProtocol = useStore((µ) => µ.candidate.control?.protocol);
  const roomId = useStore((µ) => µ.roomId);
  const manufacturerConfig = useStore((µ) => µ.manufacturer.config);
  const videoProtocol = useStore((µ) => µ.candidate.video?.protocol);

  const { track } = useAnalytics();

  const handleBack = useLatestFn(() => {
    producer((draft) => {
      // Mirror the forward routing (video/footer.tsx): RTSP-Assist flows jumped
      // Video → Control (brand/model on the RTSP screen), so Back returns to
      // Video. Non-RTSP sources came through the Manufacturer (driver) screen,
      // so Back returns there.
      draft.panel = isRtsp(videoProtocol) ? Panel.Video : Panel.Manufacturer;
    });
  });

  const handleNext = useLatestFn(() => {
    if (!isReady) {
      return;
    }

    if (blocked) {
      return;
    }

    if (sourceSelection) {
      const model = manufacturerConfig?.model;
      const deviceAnalyticsCatalogContext = resolveDeviceAnalyticsCatalogContextFromModel(model);

      // Per ENG-3960 §1.7 — additively carry is_embedded + linking_surface on the
      // embedded flow's reuse of this final shared step; non-embedded path unchanged.
      track(EVENT_NAMES.DEVICE_CONTINUE_FROM_CONTROL, EVENT_CATEGORIES.DEVICE, {
        deviceId: sourceSelection.id,
        studio_id: roomId,
        ...(controlProtocol && { controlProtocol: controlProtocol.toString() }),
        ...deviceAnalyticsCatalogContext,
        ...resolveEmbeddedFlowProps(isEmbeddedDiscovery(sourceSelection))
      });
    }

    void wiring.handleCommit();
  });

  // An unconfirmed ('uncertain') save may already have persisted, so the operator must check the
  // source list before re-adding rather than blindly retrying into a duplicate.
  const uncertain = finalSave?.status === 'uncertain';

  return (
    <_Footer.Root {...props}>
      {/* Back stays enabled during a save: B1 (attempt ownership in wiring.ts) already makes a
          navigate-away safe, and the add-flow tests exercise exactly that path. */}
      <_Footer.Back onClick={handleBack} />
      <div className='flex items-center gap-16'>
        {uncertain && (
          <span className='text-sm text-muted'>Check the source list before adding again.</span>
        )}
        <_Footer.Next
          disabled={!isReady || blocked}
          label={
            finalSave?.status === 'retryable'
              ? 'Try again'
              : finalSave?.status === 'pending'
                ? 'Saving…'
                : 'Save and Finish'
          }
          onClick={handleNext}
          {...STag(Sel.ConfigVideoDeviceControl, 'footer.continue')}
        />
      </div>
    </_Footer.Root>
  );
};
